import { useMemo } from 'react'

/**
 * useMessageFollowUp
 *
 * Determines whether a follow-up CTA should be displayed for the current chat thread.
 *
 * A follow-up is warranted when:
 *   - The last message in the conversation was sent by the current user (from_user_id === currentUserId)
 *   - No reply has been received since then (is_replied === 0 on that message)
 *   - More than 7 days have elapsed since that message
 *
 * @param {Array}  messages       - The chat messages array from the store (selectedUserRdChat.data or selectedUserBrandChat.data)
 * @param {number} currentUserId  - The currently authenticated user's ID
 * @returns {{ showFollowUp: boolean, daysSince: number }}
 */
const useMessageFollowUp = (messages, currentUserId) => {
    return useMemo(() => {
        if (!messages || messages.length === 0 || !currentUserId) {
            return { showFollowUp: false, daysSince: 0 }
        }

        // Get the last message in the thread
        const lastMessage = messages[messages.length - 1]

        const sentByMe = lastMessage.from_user_id === currentUserId
        const noReply = lastMessage.is_replied === 0 || lastMessage.is_replied === '0'

        if (!sentByMe || !noReply) {
            return { showFollowUp: false, daysSince: 0 }
        }

        const sentAt = new Date(lastMessage.created_at)
        const now = new Date()
        const diffMs = now - sentAt
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

        if (diffDays >= 7) {
            return { showFollowUp: true, daysSince: diffDays }
        }

        return { showFollowUp: false, daysSince: diffDays }
    }, [messages, currentUserId])
}

export default useMessageFollowUp
